import api from './api';

export interface Skill {
  id: number;
  name: string;
  description: string;
  tags: string[];
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  order: number;
  container: number | null;
  reference_patterns?: number[];
  reference_patterns_details?: Array<{id: number; title: string; container: number | null; container_title: string | null}>;
  is_public: boolean;
  parent?: number | null;
  children_skills?: ChildSkill[];
}

export interface ChildSkill {
  id: number;
  name: string;
  is_reference: boolean;
  skill_link_id: number | null;
  reference_patterns_details: Array<{id: number; title: string; container: number | null; container_title: string | null}>;
  children_skills: ChildSkill[];
}

export interface SkillFlat {
  id: number;
  name: string;
  description: string;
  is_public: boolean;
  order: number;
  container: number | null;
  parent: number | null;
  children_ids: number[];
  child_link_ids: Array<{ skill_id: number; link_id: number }>;
  reference_patterns_details: Array<{id: number; title: string; container: number | null; container_title: string | null}>;
}

export interface SkillSearchResult {
  id: number;
  name: string;
  container_title: string | null;
}

class SkillService {
  async getAll(params?: any): Promise<Skill[]> {
    const response = await api.get('/skills/', { params });
    if (response.data && response.data.results) {
      return response.data.results;
    }
    return response.data;
  }

  async getSkill(id: number): Promise<Skill> {
    const response = await api.get(`/skills/${id}/`);
    return response.data;
  }

  async create(data: Partial<Skill>): Promise<Skill> {
    const response = await api.post('/skills/', data);
    return response.data;
  }

  async update(id: number, data: Partial<Skill>): Promise<Skill> {
    const response = await api.patch(`/skills/${id}/`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/skills/${id}/`);
  }

  async reorder(items: { id: number; order: number }[]): Promise<void> {
    await api.post('/skills/reorder/', { items });
  }

  async getAvailableSkills(): Promise<Skill[]> {
    const response = await api.get('/skills/available/');
    return response.data;
  }

  async addReference(skillId: number, childSkillId: number): Promise<any> {
    const response = await api.post(`/skills/${skillId}/add_reference/`, { child_skill_id: childSkillId });
    return response.data;
  }

  async removeReference(skillId: number, childSkillId?: number, skillLinkId?: number): Promise<void> {
    await api.post(`/skills/${skillId}/remove_reference/`, {
      child_skill_id: childSkillId,
      skill_link_id: skillLinkId,
    });
  }

  async searchSkills(query: string): Promise<SkillSearchResult[]> {
    const response = await api.get('/skills/search/', { params: { q: query } });
    return response.data;
  }

  /** Fetch skills flat (no recursive children) - roots of a container or specific IDs */
  async getFlat(params: { container?: number | string; ids?: number[] }): Promise<SkillFlat[]> {
    const queryParams: any = {};
    if (params.container !== undefined) queryParams.container = params.container;
    if (params.ids && params.ids.length > 0) queryParams.ids = params.ids.join(',');
    const response = await api.get('/skills/flat/', { params: queryParams });
    return response.data;
  }
}

const skillService = new SkillService();
export default skillService;
